import React, { useCallback, useEffect, useMemo } from 'react';
import { Components, ExtendedComponentSchema } from 'formiojs';
import {
  EhsFormioReactComponent,
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { Component } from '@formio/react';
import { Select, TextInput } from '../../../form';
import { intl, LoadingStore } from '../../../../lib';
import { infrastructurePortalUtil } from 'generated-ts-common';
import { LanguageProperty } from '../../../generated/LanguageProperty';
import { Maskable } from '../../../basic';
import { HBox } from '../../../layout';
import shallow from 'zustand/shallow';
import { UseStore } from 'zustand';

type GenCode = infrastructurePortalUtil.saf.infrastructure.model.svs.GenCode;

/**
 * A store for retrieving and storing valuesets for the EhsFormioSVSSelect component.
 *
 * @author agd
 */
export type EhsFormioSVSSelectStore = Omit<
  LoadingStore<string>,
  'wasLoaded' | 'loading'
> & {
  /** data expected to be a Map of valueSetOid to list of retrieved codes */
  data: Record<string, Array<GenCode>>;
  /** If data was loaded for the given valueSetOid. */
  wasLoaded: (valueSetOid: string) => boolean;
  /** loading state, which stores whether a specific valueSetOid is currently loading or not. */
  loading: Record<string, boolean>;
  /** Whether the given valueSetOid is currently loading. */
  isLoading: (valueSetOid: string) => boolean;
};

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioSVSSelectConstructor = {
  valueSetOid: string;
  includeOther: boolean;
  otherLabel: string;
};
export type EhsFormioSVSSelectSettings = {
  valueSetOid: string;
  includeOther: boolean;
  otherLabel: string;
  store?: UseStore<EhsFormioSVSSelectStore>;
};
export const EhsFormioSVSSelectType = 'ehssvsselect';

export class EhsFormioSVSSelect extends EhsFormioReactComponent<
  EhsFormioSVSSelectSettings,
  EhsFormioSVSSelectConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioSVSSelectType,
      key: EhsFormioSVSSelectType,
      ...extend
    });
  }

  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-SVSSelect',
      group: 'advanced',
      icon: 'file',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioSVSSelect.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'valueSetOid',
          label: 'valueSetOid'
        },
        {
          type: 'checkbox',
          input: true,
          key: 'includeOther',
          label: 'Include Other Option',
          tooltip:
            'Enabling this will add an Other option to the field which, when selected, opens a textfield to enter the value.',
          weight: 30,
          defaultValue: true
        },
        {
          type: 'textfield',
          key: 'otherLabel',
          label: 'other field label',
          tooltip: 'Please add only valid translation keys',
          defaultValue: 'common.value'
        }
      ]
    };
  }

  private includeOther = true;
  private valueSetOid = '';
  private otherLabel = 'common.value';

  //Begin Non static definitions-------------------------------------
  extractFields(component: Component<EhsFormioSVSSelectConstructor>): void {
    this.valueSetOid = component.valueSetOid;
    this.includeOther = component.includeOther;
    this.otherLabel = component.otherLabel;
  }

  static store?: UseStore<EhsFormioSVSSelectStore>;

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioSVSSelectSettings>) => JSX.Element,
    EhsFormioSVSSelectSettings
  ] {
    return [
      SVSSelectCmp,
      {
        valueSetOid: this.valueSetOid,
        includeOther: this.includeOther,
        otherLabel: this.otherLabel,
        store: EhsFormioSVSSelect.store
      }
    ];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link EhsFormioSVSSelectSettings} properties
 */
function SVSSelectCmp({
  label,
  value,
  onChange,
  readOnly,
  settings
}: EhsFormioReactProps<EhsFormioSVSSelectSettings>): JSX.Element {
  const otherValueIndicator = 'OTHER___';
  // "Other" option in the Select
  const otherOption: GenCode = useMemo(
    () => ({
      value: 'OTHER',
      codingScheme: 'OTHER',
      displayName: intl('common.other'),
      valueMappedAsHL7: otherValueIndicator
    }),
    []
  );
  const [storeData, loadSVSValues, isLoading] = settings.store?.(
    state => [state.data, state.load, state.isLoading],
    shallow
  ) ?? [{}, () => null, () => false]; // empty state when the store is not set yet

  // automatically load the items, if the url is initialized properly
  useEffect(() => {
    if (!storeData[settings.valueSetOid]) {
      loadSVSValues(settings.valueSetOid);
    }
  }, [storeData, loadSVSValues, settings.valueSetOid]);

  const items: Array<GenCode> = useMemo(
    () => storeData[settings.valueSetOid] ?? [],
    [settings.valueSetOid, storeData]
  );
  const selectItems: Array<GenCode> = useMemo(() => {
    return settings.includeOther ? [...items, otherOption] : items;
  }, [items, otherOption, settings.includeOther]);

  // wrapper for when the value changes. To map the selected value to a string using valueMappedAsHL7
  const onChangeWrapper = useCallback(
    (value: GenCode | null): void => {
      onChange(value?.valueMappedAsHL7 ?? '');
    },
    [onChange]
  );
  // wrapped value which will either find the correct GenCode by comparing the valueMappedAsHL7 or use the other value if the value starts with the correct string
  const wrapperValue = useMemo(() => {
    return settings.includeOther && value?.startsWith(otherValueIndicator)
      ? otherOption
      : items.find(item => item.valueMappedAsHL7 === value) ?? null;
  }, [items, otherOption, settings.includeOther, value]);

  return useMemo(() => {
    return (
      <Maskable
        className="ehs-formio-svs-select"
        masked={isLoading(settings.valueSetOid)}
      >
        <HBox>
          <Select
            className="ehs-formio-svs-select-select"
            value={wrapperValue}
            onChange={onChangeWrapper}
            label={intl(label as LanguageProperty)}
            itemValueProp="valueMappedAsHL7"
            itemLabelProp="displayName"
            variant="single"
            items={selectItems}
            disabled={readOnly}
          />
          {settings.includeOther && value?.startsWith(otherValueIndicator) && (
            <TextInput
              type="text"
              label={intl(settings.otherLabel as LanguageProperty)}
              value={value.substring(otherValueIndicator.length)}
              onChange={newValue => {
                // the thing that's saved as the value is "OTHER___<value in the text field>"
                onChangeWrapper({
                  ...otherOption,
                  valueMappedAsHL7: otherValueIndicator + newValue
                });
              }}
              disabled={readOnly}
            />
          )}
        </HBox>
      </Maskable>
    );
  }, [
    isLoading,
    label,
    onChangeWrapper,
    otherOption,
    readOnly,
    selectItems,
    settings.includeOther,
    settings.otherLabel,
    settings.valueSetOid,
    value,
    wrapperValue
  ]);
}
